import { Injectable, inject } from "@angular/core";
import { Action, NgxsAfterBootstrap, Selector, State, StateContext } from "@ngxs/store";
import { iif, insertItem, patch, updateItem } from "@ngxs/store/operators";
import { ApplicationState } from "@vp/data-access/application";
import { ContentApiService } from "@vp/data-access/content-management";
import { ContentData, User } from "@vp/models";
import { AuthenticationService } from "@vp/shared/authentication";
import { map, take, tap } from "rxjs";
import * as SystemBannerActions from "./system-banner-action";

export interface SystemBannerStateModel {
  globalAlerts: ContentData[];
  dismissedAlerts: string[];
}

@State<SystemBannerStateModel>({
  name: "systemBanner",
  defaults: {
    globalAlerts: [],
    dismissedAlerts: []
  }
})
@Injectable()
export class SystemBannerState implements NgxsAfterBootstrap {
  private contentApiService = inject(ContentApiService);
  private authenticationService = inject(AuthenticationService);

  ngxsAfterBootstrap(ctx: StateContext<any>): void {
    this.authenticationService
      .isLoggedIn$()
      .pipe(take(1))
      .subscribe(authenticatedResult => {
        if (authenticatedResult.isAuthenticated) {
          ctx.dispatch(new SystemBannerActions.LoadGlobalAlerts());
        }
      });
  }

  @Selector()
  static getGlobalAlerts(state: SystemBannerStateModel) {
    return state.globalAlerts;
  }

  @Selector()
  static dismissedAlerts(state: SystemBannerStateModel) {
    return state.dismissedAlerts;
  }

  @Selector([
    SystemBannerState.getGlobalAlerts,
    SystemBannerState.dismissedAlerts,
    ApplicationState.loggedInUser
  ])
  static globalAlerts(
    globalAlerts: ContentData[],
    dismissedAlerts: string[],
    loggedInUser: User | null
  ) {
    return globalAlerts
      .filter(alert => !dismissedAlerts.includes(alert.contentId))
      .filter(alert => this.IsGlobalAlertActive(alert))
      .filter(alert => {
        return (
          alert.tags.length === 0 ||
          alert.tags.some(tag => loggedInUser?.assignedTags?.includes(tag))
        );
      });
  }

  @Action(SystemBannerActions.LoadGlobalAlerts)
  loadGlobalAlerts(ctx: StateContext<SystemBannerStateModel>) {
    return this.contentApiService.filteredContents({ contentTypeId: "global-alert" }).pipe(
      map(pageResult => pageResult.results),
      tap(globalAlerts => {
        ctx.patchState({ globalAlerts: globalAlerts });
      })
    );
  }

  @Action(SystemBannerActions.DismissAlert)
  dismissAlert(
    ctx: StateContext<SystemBannerStateModel>,
    action: SystemBannerActions.DismissAlert
  ) {
    const dismissedAlerts = [...ctx.getState().dismissedAlerts, action.alertId];
    ctx.patchState({ dismissedAlerts: dismissedAlerts });
  }

  @Action(SystemBannerActions.AddGlobalAlert)
  addGlobalAlert(
    ctx: StateContext<SystemBannerStateModel>,
    action: SystemBannerActions.AddGlobalAlert
  ) {
    ctx.setState(
      patch<SystemBannerStateModel>({
        globalAlerts: insertOrUpdate(action.alert.contentId, action.alert)
      })
    );
  }

  private static IsGlobalAlertActive(alert: ContentData) {
    const now = new Date();
    const start = alert.schedule?.start ? new Date(alert.schedule.start) : null;
    const end = alert.schedule?.end ? new Date(alert.schedule.end) : null;
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  }
}

const insertOrUpdate = (contentId: string, alert: ContentData) => {
  return iif<ContentData[]>(
    alerts => alerts?.some(c => c.contentId === contentId) ?? false,
    updateItem<ContentData>(item => item?.contentId === contentId, patch(alert)),
    insertItem<ContentData>(alert)
  );
};
